import { useCallback, useRef, useEffect, useState } from 'react';

// ─── 모듈 레벨 Audio 캐시 ────────────────────────────────────────────────────
// 앱 생애주기 동안 유지. 동일 URL이면 재다운로드하지 않고 재사용함
const audioCache = new Map<string, HTMLAudioElement>();

function getCachedAudio(url: string, volume: number): HTMLAudioElement {
    if (!audioCache.has(url)) {
        const audio = new Audio(url);
        audio.preload = 'auto';
        audio.load(); // 백그라운드에서 즉시 다운로드 시작
        audioCache.set(url, audio);
    }
    const audio = audioCache.get(url)!;
    audio.volume = Math.max(0, Math.min(1, volume / 100));
    return audio;
}
// ─────────────────────────────────────────────────────────────────────────────

interface NotificationSoundOptions {
    volume: number;
    soundUrl: string;
    enableSound: boolean;
}

// 🔧 브라우저 알림을 위한 새로운 옵션들 추가
interface BrowserNotificationOptions {
    title: string;
    body: string;
    icon?: string;
    tag?: string;
    requireInteraction?: boolean;
    silent?: boolean;
    badge?: string;
    onClick?: () => void;
}

interface NotificationHookOptions extends NotificationSoundOptions {
    enableBrowserNotification?: boolean; // 🔧 브라우저 알림 활성화 여부
}

export const useNotificationSound = ({
    volume,
    soundUrl,
    enableSound,
    enableBrowserNotification = false // 🔧 기본값 false
}: NotificationHookOptions) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const [userInteracted, setUserInteracted] = useState(false);

    // soundUrl이 바뀌면 즉시 사전 다운로드 (캐시에 없을 때만)
    useEffect(() => {
        if (!soundUrl) return;
        if (!audioCache.has(soundUrl)) {
            const audio = new Audio(soundUrl);
            audio.preload = 'auto';
            audio.load();
            audioCache.set(soundUrl, audio);
        }
    }, [soundUrl]);

    // 🔧 사용자 상호작용 감지 및 오디오 컨텍스트 활성화
    useEffect(() => {
        const handleUserInteraction = () => {
            if (!userInteracted) {
                setUserInteracted(true);

                // 오디오 컨텍스트 초기화
                try {
                    if (!audioContextRef.current) {
                        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
                    }

                    if (audioContextRef.current.state === 'suspended') {
                        audioContextRef.current.resume().then(() => {
                            if (process.env.NODE_ENV === 'development') {
                                console.log('🔊 오디오 컨텍스트 활성화됨');
                            }
                        });
                    }
                } catch (error) {
                    console.error('오디오 컨텍스트 초기화 실패:', error);
                }
            }
        };

        // 다양한 사용자 상호작용 이벤트 리스너 등록
        const events = ['click', 'touchstart', 'keydown'];
        events.forEach(event => {
            document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
        });

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserInteraction);
            });
        };
    }, [userInteracted]);

    // 🔧 Web Audio API로 간단한 비프음 생성 (fallback)
    const playBeepSound = useCallback(() => {
        try {
            // 새로 생성한 context를 반드시 ref에 저장해야 재사용 가능
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            }
            const audioContext = audioContextRef.current;

            const doPlay = () => {
                try {
                    const oscillator = audioContext.createOscillator();
                    const gainNode = audioContext.createGain();

                    oscillator.connect(gainNode);
                    gainNode.connect(audioContext.destination);

                    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                    gainNode.gain.setValueAtTime(volume / 100 * 0.3, audioContext.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

                    oscillator.start(audioContext.currentTime);
                    oscillator.stop(audioContext.currentTime + 0.3);

                    if (process.env.NODE_ENV === 'development') {
                        console.log('🔊 비프음 재생 (fallback)');
                    }
                } catch (e) {
                    console.error('❌ oscillator 재생 실패:', e);
                }
            };

            // suspended 상태면 resume 완료 후 재생 (await 없이 then 체인으로 처리)
            if (audioContext.state === 'suspended') {
                audioContext.resume().then(doPlay).catch((e) => {
                    console.error('❌ AudioContext resume 실패:', e);
                });
            } else {
                doPlay();
            }

            return true;
        } catch (error) {
            console.error('❌ 비프음 생성 실패:', error);
            return false;
        }
    }, [volume]);

    // 🔧 모바일 및 브라우저별 오디오 지원 여부 체크
    const checkAudioSupport = useCallback(() => {
        const userAgent = navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        const isAndroid = /Android/.test(userAgent);
        const isMobile = isIOS || isAndroid;
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches; // PWA 모드

        return {
            isIOS,
            isAndroid,
            isMobile,
            isStandalone,
            canAutoplay: !isMobile || isStandalone, // PWA에서는 더 자유로움
            recommendFallback: isIOS && !isStandalone // iOS Safari는 fallback 권장
        };
    }, []);

    // 🔧 소리 재생 함수 (브라우저 알림과 완전 분리)
    const playSound = useCallback(() => {
        if (!enableSound) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🔕 소리 재생 스킵: enableSound = false');
            }
            return Promise.resolve(false);
        }

        const audioSupport = checkAudioSupport();

        if (process.env.NODE_ENV === 'development') {
            console.log('🔊 오디오 환경 체크:', audioSupport);
        }

        // 🔧 soundUrl이 없거나 모바일에서 fallback 권장 시 비프음 사용
        if (!soundUrl || audioSupport.recommendFallback) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🔊 비프음 사용:', audioSupport.recommendFallback ? 'iOS fallback' : 'soundUrl 없음');
            }
            return Promise.resolve(playBeepSound());
        }

        return new Promise<boolean>((resolve) => {
            try {
                if (process.env.NODE_ENV === 'development') {
                    console.log('🔊 소리 재생 시도:', {
                        soundUrl,
                        volume: `${volume}%`,
                        enableSound,
                        userInteracted,
                        mobile: audioSupport.isMobile,
                        canAutoplay: audioSupport.canAutoplay
                    });
                }

                // 🔧 사용자 상호작용이 없고 모바일인 경우 경고
                if (!userInteracted && audioSupport.isMobile) {
                    console.warn('⚠️ 모바일에서 사용자 상호작용 없이 오디오 재생 시도');
                }

                // 캐시에서 Audio 가져오기 (없으면 새로 생성 후 캐시에 저장)
                const audio = getCachedAudio(soundUrl, volume);

                if (audioRef.current && audioRef.current !== audio) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                }
                audioRef.current = audio;
                audio.currentTime = 0;

                // 🔧 모바일 최적화 설정
                if (audioSupport.isMobile) {
                    audio.crossOrigin = 'anonymous';
                }

                audio.onerror = (e) => {
                    console.error('❌ 오디오 파일 로드 실패:', e, soundUrl);
                    // 캐시에서 제거해 다음 시도 시 재다운로드
                    audioCache.delete(soundUrl);
                    const fallbackSuccess = playBeepSound();
                    resolve(fallbackSuccess);
                };

                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            if (process.env.NODE_ENV === 'development') {
                                console.log('✅ 소리 재생 성공');
                            }
                            resolve(true);
                        })
                        .catch((error) => {
                            console.error('❌ 소리 재생 실패:', error);

                            // 🔧 브라우저 자동재생 정책 에러 처리
                            if (error.name === 'NotAllowedError') {
                                console.warn('⚠️ 브라우저 자동재생 정책으로 인해 소리가 차단되었습니다.');
                                console.log(`💡 해결방법: ${audioSupport.isMobile ? '화면을 터치하거나' : '페이지를 클릭하거나'} 키를 누른 후 다시 시도하세요.`);

                                // iOS에서는 비프음도 차단될 수 있으므로 조건부 fallback
                                if (!audioSupport.isIOS || userInteracted) {
                                    console.log('🔄 비프음으로 fallback 시도');
                                    const fallbackSuccess = playBeepSound();
                                    resolve(fallbackSuccess);
                                } else {
                                    console.log('🚫 iOS에서 사용자 상호작용 없이는 비프음도 재생 불가');
                                    resolve(false);
                                }
                            } else {
                                console.log('🔄 비프음으로 fallback 시도');
                                const fallbackSuccess = playBeepSound();
                                resolve(fallbackSuccess);
                            }
                        });
                } else {
                    resolve(false);
                }
            } catch (error) {
                console.error('❌ 소리 초기화 실패:', error);
                console.log('🔄 비프음으로 fallback 시도');
                const fallbackSuccess = playBeepSound();
                resolve(fallbackSuccess);
            }
        });
    }, [enableSound, soundUrl, volume, playBeepSound, userInteracted, checkAudioSupport]);

    // �� 브라우저 알림 생성 함수 (새로 추가)
    const showBrowserNotification = useCallback((options: BrowserNotificationOptions) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🔔 showBrowserNotification:', {
                enableBrowserNotification,
                hasAPI: 'Notification' in window,
                permission: 'Notification' in window ? Notification.permission : 'N/A',
                title: options.title,
            });
        }

        if (!enableBrowserNotification || !('Notification' in window)) {
            return false;
        }

        if (Notification.permission === 'granted') {
            try {
                const notification = new Notification(options.title, {
                    body: options.body,
                    icon: options.icon || '/favicon.ico',
                    tag: options.tag || `notification-${Date.now()}`,
                    requireInteraction: options.requireInteraction || false,
                    silent: options.silent || false,
                    badge: options.badge || '/favicon.ico',
                });

                if (options.onClick) {
                    notification.onclick = () => {
                        window.focus();
                        notification.close();
                        options.onClick?.();
                    };
                }

                return true;
            } catch (error) {
                console.error('❌ 브라우저 알림 생성 실패:', error);
                return false;
            }
        } else if (Notification.permission === 'default') {
            // 🔧 권한 요청
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    // 권한 허용 후 다시 시도
                    showBrowserNotification(options);
                }
            });
            return false;
        }

        return false;
    }, [enableBrowserNotification]);

    // 🔧 통합 알림 함수 (소리 + 브라우저 알림 동시 실행)
    const notify = useCallback((browserOptions?: BrowserNotificationOptions) => {
        // 소리 재생
        playSound();

        // 브라우저 알림 표시
        if (browserOptions) {
            showBrowserNotification(browserOptions);
        }
    }, [playSound, showBrowserNotification]);

    // �� 개발 환경에서 소리 테스트 함수
    const testSound = useCallback(() => {
        console.log('🔊 소리 테스트 시작');
        console.log('설정:', {
            enableSound,
            soundUrl,
            volume: `${volume}%`,
            userInteracted,
            audioContextState: audioContextRef.current?.state || 'not created'
        });

        return playSound();
    }, [playSound, enableSound, soundUrl, volume, userInteracted]);

    return {
        playSound,
        showBrowserNotification, // 🔧 브라우저 알림만 따로 사용 가능
        notify, // 🔧 소리 + 브라우저 알림 통합 함수
        testSound, // 🔧 개발용 테스트 함수
        userInteracted, // 🔧 사용자 상호작용 여부
    };
}; 